import i18next from 'i18next'
import React from 'react'
import { Link } from '../../utils/link'
import { DecorativeIcon } from '../../utils/spaarneIcons'
import { slugify } from '../../utils/utils'
import { useInView } from '../hooks/useInView'
import { ViewListenerState } from '../viewListener/viewListenerState'

// ----- Skiplinks

export const SkipLinks = () => (
  <>
    <a href="#a11y-main" className="sr-text">
      {i18next.t('a11y:skipToContent')}
    </a>
    <a href="#a11y-footer" className="sr-text">
      {i18next.t('a11y:skipToFooter')}
    </a>
  </>
)

// ----- Header Layout

type NavItem = {
  isItemOpen: boolean
  toggleItem: () => void
  title: string
  children: {
    title: string
    url: string
  }[]
}

export const HeaderLayout = (props: {
  items: NavItem[]
  isMenuOpen: boolean
  toggleMenu: () => void
  view: ViewListenerState
}) => {
  const { items, isMenuOpen, toggleMenu, view } = props
  const isLinkActive = (url: string) => url === window.location.pathname.toLocaleLowerCase()
  const toggleMobileMenu = () => {
    document.documentElement.classList.toggle('nav--toggled')
    toggleMenu()
  }
  const closeMobileMenu = () => {
    document.documentElement.classList.remove('nav--toggled')
    toggleMenu()
  }
  const isSubMenuOpen = (item: NavItem) => item.isItemOpen
  const isSubMenuActive = (item: NavItem): boolean =>
    item.children.filter((c) => c.url).find((c) => c.url === window.location.pathname.toLocaleLowerCase())
      ? true
      : false

  const isSomeSubMenuOpened = items.find((item) => item.isItemOpen) ? true : false
  const selectOpenSubMenu = isSomeSubMenuOpened ? items.find((item) => item.isItemOpen) : undefined

  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("HeaderLayout inView:", inview),
    false
  )

  return (
    <>
      <SkipLinks />
      <header
        key={`block-header`}
        id={`io-header`}
        ref={ref}
        className={`header ${isSomeSubMenuOpened ? 'header--submenu' : ''}` + ' ' + (inView ? 'inview' : '')}
      >
        <div className="header__outer">
          <div className="utility__section--background"></div>
          <div className="header__inner">
            <div className="utility__section--background"></div>
            <div className="header__wrapper">
              {/* ----- Toolbar & Toggler */}

              <div className="header__toolbar">
                <Link
                  className="header__logo"
                  url={i18next.language == 'nl' ? '/' : '/en'}
                  additionalOnClick={() => {
                    closeMobileMenu()
                    selectOpenSubMenu ? selectOpenSubMenu.toggleItem() : undefined
                  }}
                  title={i18next.t('a11y:navigateHome')}
                >
                  <span className="sr-text">{i18next.t('a11y:navigateHome')}</span>
                  <img
                    className="header__image"
                    alt="SpaarneGasthuis logo"
                    src="/images/logo--color.svg"
                    width="200"
                    height="30"
                    aria-hidden="true"
                  />
                </Link>

                <button
                  className="header__toggler"
                  aria-controls="a11y-main-menu-collapse"
                  aria-expanded={isMenuOpen}
                  onClick={() => toggleMobileMenu()}
                >
                  <span className="sr-text">
                    {i18next.t('a11y:toggleMenu', {
                      toggle: !isMenuOpen ? i18next.t('common:open') : i18next.t('common:close'),
                    })}
                  </span>
                  <span aria-hidden="true" />
                  <span aria-hidden="true" />
                  <span aria-hidden="true" />
                  <span aria-hidden="true" />
                </button>
              </div>

              <nav
                className={`header__nav nav--header ${isSomeSubMenuOpened ? 'nav--sub-menu-open' : ''}`}
                id="a11y-main-menu-collapse"
                aria-label={i18next.t('a11y:navLabelMain')}
              >
                {/* ----- Main menu */}

                <div className="nav__section nav__section--main">
                  <ul className={`nav__items nav__items--${items.length > 2 ? 'align' : 'no-align'}`}>
                    {items.map((item) => {
                      const isOpen = isSubMenuOpen(item);
                      return (<li
                        key={slugify(item.title)}
                        className={`nav__item${isOpen ? ' nav__item--open' : ' nav__item--closed'}`}
                      >
                        {view == 'desktop' || !isOpen ? (
                          <button
                            title={`${isOpen ? i18next.t('common:open') : i18next.t('common:close')} ${item.title
                              }`}
                            className={`nav__link nav__button${isOpen || isSubMenuActive(item) ? ' nav__link--active' : ''
                              }`}
                            aria-haspopup={true}
                            aria-expanded={isOpen}
                            aria-controls={`a11y-nav-sub-${slugify(item.title)}`}
                            onClick={item.toggleItem}
                          >
                            <span>{item.title}</span>
                            <svg className="nav__icon" aria-hidden="true">
                              <use xlinkHref="#icon--chevron-down" />
                            </svg>
                          </button>
                        ) : (
                          <button
                            title={`${isOpen ? i18next.t('common:open') : i18next.t('common:close')} ${item.title
                              }`}
                            className="nav__link nav__button nav__return"
                            aria-haspopup={true}
                            aria-expanded={isOpen}
                            aria-controls={`a11y-nav-sub-${slugify(item.title)}`}
                            onClick={item.toggleItem}
                          >
                            <span>{i18next.t('common:return')}</span>
                            <svg className="nav__icon" aria-hidden="true">
                              <use xlinkHref="#icon--chevron-down" />
                            </svg>
                          </button>
                        )}

                        {/* ----- Sub-menu */}

                        <div
                          id={`a11y-nav-sub-${slugify(item.title)}`}
                          className={`nav__sub ${isOpen ? 'nav__sub-open' : 'nav__sub-closed'}`}
                        >
                          <ul className="nav__sub-items">
                            {item.children.map((child) => (
                              <li key={slugify(child.title)} className="nav__item nav__sub-item">
                                <Link
                                  url={child.url}
                                  additionalOnClick={
                                    //This onClick menu executes two methods: 1) Closes the menu on mobile phones, 2) Closes the menu on a pc.
                                    () => {
                                      closeMobileMenu()
                                      item.toggleItem()
                                    }
                                  }
                                  className={`nav__link nav__sub-link${isLinkActive(child.url) ? ' nav__link--active' : ''
                                    }`}
                                  aria-current={isLinkActive(child.url) ? 'page' : undefined}
                                >
                                  <span>{child.title}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </li>)
                    }
                    )}

                    <li key={'zoeken-menu-item'} className="nav__item  nav__item--closed nav__item-as-icon">
                      <Link
                        url={'/zoeken'}
                        title={'Zoeken'}
                        additionalOnClick={() => {
                          closeMobileMenu()
                          selectOpenSubMenu ? selectOpenSubMenu.toggleItem() : undefined
                        }}
                        className={window.innerWidth < 1280 ? 'nav__link' : 'nav__link nav__button'}
                      // tabIndex={view != 'desktop' && !isMenuOpen ? -1 : 0}
                      >
                        <span>Zoeken</span>
                        <svg
                          className={window.innerWidth < 1280 ? 'zoeken_icon' : 'button__icon button__icon--search'}
                          width="25px"
                          height="25px"
                          aria-hidden="true"
                        >
                          <use xlinkHref={'#icon--SG-search-outlined'} />
                        </svg>
                      </Link>
                    </li>
                  </ul>
                </div>

                {/* ----- Utility menu */}

                <div className="nav__section nav__section--utility">
                  <ul className="nav__items">
                    {[
                      {
                        title: i18next.t('header:werkenEnLerenText'),
                        url: i18next.t('header:werkenEnLerenUrl'),
                        icon: 'icon--SG-weken-en-leren',
                      },
                      {
                        title: i18next.t('header:contactText'),
                        url: i18next.t('header:contactUrl'),
                        icon: 'icon--SG-contact-outlined',
                      },
                      {
                        title: i18next.t('header:loginText'),
                        url: i18next.t('header:loginUrl'),
                        icon: 'icon--SG-user-outlined',
                      },
                    ].map((item) => (
                      <li key={slugify(item.title)} className="nav__item nav__item--closed">
                        <Link
                          url={item.url}
                          title={item.title}
                          additionalOnClick={() => {
                            closeMobileMenu()
                            selectOpenSubMenu ? selectOpenSubMenu.toggleItem() : undefined
                          }}
                          className={window.innerWidth < 1280 ? 'nav__link' : 'nav__link--utility'}
                          aria-current={item.url === window.location.pathname.toLocaleLowerCase() ? 'page' : undefined}
                        // tabIndex={view != 'desktop' && !isMenuOpen ? -1 : 0}
                        >
                          <span>{item.title}</span>
                          <DecorativeIcon iconId={item.icon} className={window.innerWidth < 1280 ? 'nav__icon' : 'button__icon'} width="20px" height="20px" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </nav>
            </div>
          </div>
        </div>
        <div
          className="header__underlay"
          aria-hidden="true"
          onClick={() => {
            selectOpenSubMenu ? selectOpenSubMenu.toggleItem() : undefined
          }}
        />
      </header>
    </>
  )
}
